import { useRef, type ComponentType, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import { Pressable } from '../Common/Pressable'
import type { ChecklistDetailSectionId } from '../../config/checklistDetailSections'

export type NodeDetailTabId = ChecklistDetailSectionId
type NodeDetailTabIcon = ComponentType<SvgIconProps>

export type NodeDetailTab = {
  id: NodeDetailTabId
  label: string
  icon: NodeDetailTabIcon
  iconActive: NodeDetailTabIcon
  chip?: ReactNode
  tourId?: string
}

export const getNodeDetailTabDomIds = (id: NodeDetailTabId) => ({
  tabId: `node-detail-tab-${id}`,
  panelId: `node-detail-tab-panel-${id}`,
})

interface NodeDetailTabStripProps {
  tabs: NodeDetailTab[]
  activeId: NodeDetailTabId
  onSelect: (id: NodeDetailTabId) => void
}

export const NodeDetailTabStrip = ({ tabs, activeId, onSelect }: NodeDetailTabStripProps) => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
  const tabStripStyle = { '--tab-count': tabs.length } as CSSProperties

  const focusTabAt = (index: number) => {
    const nextTab = tabRefs.current[index]
    nextTab?.focus()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (tabs.length === 0) return
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      focusTabAt((index + 1) % tabs.length)
      return
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      focusTabAt((index - 1 + tabs.length) % tabs.length)
      return
    }
    if (event.key === 'Home') {
      event.preventDefault()
      focusTabAt(0)
      return
    }
    if (event.key === 'End') {
      event.preventDefault()
      focusTabAt(tabs.length - 1)
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect(tabs[index].id)
    }
  }

  return (
    <div className="nodeDetailTabStrip" role="tablist" aria-label="Checklist detail sections" style={tabStripStyle}>
      {tabs.map((tab, index) => {
        const selected = tab.id === activeId
        const { tabId, panelId } = getNodeDetailTabDomIds(tab.id)
        const Icon = selected ? tab.iconActive : tab.icon
        return (
          <Pressable
            key={tab.id}
            data-tour-id={tab.tourId}
            ref={(element) => {
              tabRefs.current[index] = element
            }}
            id={tabId}
            role="tab"
            className={`nodeDetailTab ${selected ? 'isActive' : ''}`}
            aria-label={tab.label}
            title={tab.label}
            aria-selected={selected}
            aria-controls={panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(event) => handleKeyDown(event, index)}
          >
            <span className="nodeDetailTabIcon" aria-hidden="true">
              <Icon fontSize="inherit" />
            </span>
            <span className="nodeDetailTabChip">{tab.chip ?? '\u00A0'}</span>
          </Pressable>
        )
      })}
    </div>
  )
}
